import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import ArtifactsModule from '../workflowModule/ArtifactsModule';
import { BuildArtifact } from '@/types';
import {
    CheckOutlined,
    CloudDownloadOutlined,
    DownOutlined,
    EditOutlined,
    MobileOutlined,
    ShareAltOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectApp,
    selectAppDistributionServices,
    selectAppIdentifiers,
    selectAppPlatform,
    selectAppSettings,
    selectBuildDistributionBucketById,
    selectBuildDistributionBuildById,
    selectIsDemoInstance,
    selectOrgGroups,
    selectOrgUsers,
    selectPaginatedBucketBuildWorkItemsTimelineLimitOffsetAndTotalCount,
    selectShouldShowRegisterDevicesPrompt,
    selectUser,
    selectUserById,
    selectUserDevices,
} from '@/selectors/selectors';
import {
    BuildDistroBuildSharedWithMe,
    BuildDistroBuildStatus,
    BuildDistroEntityError,
    IntegrationType,
    OrgUser,
    ProvisioningProfileType,
} from '@/types';
import { antGrey, getAvatarColorForName, hexStringToRGBAString, runwayColors } from '../styles/globalStyles';
import {
    BuildBucketAvatar,
    buildDrawerMembersListItems,
    buildListDataSource,
    BuildNumberTag,
} from './CommonComponents';
import {
    DrawerList,
    DrawerSection,
    DrawerSectionsWrapper,
    ResponsiveDrawer,
} from './mobileComponents/ResponsiveDrawer';
import Form, { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import FormList from 'antd/lib/form/FormList';
import MarkdownEditor from '../HOCs/MarkdownEditor';
import {
    tryFetchEntity,
    tryFetchPaginatedEntities,
    UpdateBuildDistributionBuildTesterNotesRequest,
} from '@/actions/entities';
import {
    tryShareBuildDistributionBuildWithTesters,
    tryUpdateBuildDistributionBuildTesterNotes,
    UPDATE_BUILD_DISTRIBUTION_BUILD_TESTER_NOTES,
    UPDATE_BUILD_DISTRIBUTION_BUILD_TESTERS,
} from '@/actions/buildDistribution';
import { canPlatformInstallBinaries, getShouldDisableInstallationForBinaryType, shareLink } from '@/helpers';
import { globalMessage } from '@/utils/globalMessage';
import AuthedUrlButton from '../authedUrlButton/AuthedUrlButton';
import { isMobile } from 'react-device-detect';
import { createBuildDistroBuildRoute } from '@/helpers/routeHelpers';
import { QRCode } from 'react-qrcode-logo';
import Modal from 'antd/lib/modal';
import { trackBuildDistroViewEvent } from '@/actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import BuildDistroInstallMenu from './BuildDistroInstallMenu';
import { compact } from 'lodash';
import {
    displayUserNameLong,
    helperTextForArtifactType,
    shouldShowRegisterDevicesPromptForArtifactType,
} from '@/helpers/displayHelpers';
import Avatar from 'antd/lib/avatar';
import Dropdown from 'antd/lib/dropdown';
import { MenuProps } from 'antd/lib/menu';
import Select from 'antd/lib/select';
import { FetchableEntityType, FetchablePaginatedEntityType } from '@/entityType';
import { BuildDistroEntityErrorResult } from './EntityError';
import Spin from 'antd/lib/spin';
import Alert from 'antd/lib/alert';
import MarkdownComponent from '../HOCs/Markdown';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { WorkItemsDiffTimeline } from './WorkItemsTimeline';
import { RegisterDeviceAlert, RegisterDeviceMobileAlert } from '../RegisterDeviceAlert';
import { ArtifactUploadStatus } from '../workflowModule/ArtifactsModule';
import { PLATFORM_AVAILABILITY_MESSAGE } from '@/constants';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { BuildDistroShareMenu } from './BuildDistroShareMenu';
import ArchiveBuildButton from './ArchiveBuildButton';

export interface BuildDetailsDrawerProps {
    buildId: string | null;
    bucketId?: string;
    buildSystemIntegrationType?: IntegrationType;
    onCancel: () => void;
    appId: string;
    orgId: string;
    afterOpenChange?: (open: boolean) => void;
    isAutoMergeRunwayPRsEnabled?: boolean;
}

const EditTestersForm: React.FC<{
    orgUsers: OrgUser[];
    currentTesters: OrgUser[];
    newTesters?: string[];
    currentUserId: string;
    isLoading: boolean;
    onTestersChange: (testers: OrgUser[]) => void;
}> = ({ orgUsers, currentTesters, newTesters, currentUserId, isLoading, onTestersChange }) => {
    const [form] = useForm();

    const testerKey = 'tester';

    return (
        <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, values) => {
                const testers = values.testers as OrgUser[];
                onTestersChange(testers);
            }}
            preserve={false}
            initialValues={{ testers: currentTesters }}
        >
            <FormList name="testers">
                {(fields, { add, remove }) => (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div
                            style={{
                                padding: 16,
                                backgroundColor: antGrey.grey2,
                                borderRadius: 4,
                            }}
                        >
                            <Select
                                showSearch
                                placeholder="Add team member(s)"
                                style={{ width: '100%' }}
                                value={null}
                                defaultActiveFirstOption={false}
                                autoClearSearchValue
                                loading={isLoading}
                                disabled={isLoading}
                                onSelect={(value) => {
                                    const testerId = value as string;
                                    const tester = orgUsers.find((user) => user.id === testerId);
                                    if (tester) {
                                        add(tester);
                                    }
                                }}
                                options={orgUsers
                                    .filter(
                                        (user) =>
                                            !(newTesters ?? currentTesters?.map((t) => t.id))?.some(
                                                (testerId) => testerId === user.id,
                                            ),
                                    )
                                    .map((user) => ({
                                        label: displayUserNameLong(user),
                                        value: user.id,
                                        disabled: user.id === currentUserId,
                                    }))}
                            ></Select>
                        </div>
                        <DrawerList
                            emptyText="No individual testers yet"
                            items={fields.map((field) => {
                                const id = form.getFieldValue(['testers', field.name, 'id']);
                                const user = orgUsers.find((user) => user.id === id);

                                const onClick: MenuProps['onClick'] = ({ key }) => {
                                    if (key !== testerKey) {
                                        remove(field.name);
                                    }
                                };

                                const items: MenuProps['items'] = [
                                    {
                                        key: testerKey,
                                        label: <Text style={{ color: antGrey.grey9 }}>Tester</Text>,
                                    },
                                    {
                                        key: user.id,
                                        label: <Text style={{ color: runwayColors.statusRed }}>Remove</Text>,
                                    },
                                ];

                                return {
                                    title: <TesterRow tester={user} currentUserId={currentUserId} />,
                                    description: (
                                        <Dropdown disabled={isLoading} menu={{ items, onClick }} trigger={['click']}>
                                            <Button type="link">
                                                <Space>
                                                    Tester
                                                    <DownOutlined />
                                                </Space>
                                            </Button>
                                        </Dropdown>
                                    ),
                                };
                            })}
                        />
                    </Space>
                )}
            </FormList>
        </Form>
    );
};

const TesterRow: React.FC<{
    tester: OrgUser;
    currentUserId: string;
}> = ({ tester, currentUserId }) => {
    const name = displayUserNameLong(tester);
    const isCurrentUser = tester.id === currentUserId;
    const icon = (
        <Avatar
            style={{
                fontSize: 12,
                border: 'none',
                backgroundColor: getAvatarColorForName(name),
                flexShrink: 0,
            }}
            size={25}
            src={tester?.commsAvatars?.image32}
        >
            {name.charAt(0).toUpperCase()}
        </Avatar>
    );
    return (
        <Space>
            {icon}
            <Text
                ellipsis
                style={{
                    color: antGrey.grey8,
                }}
            >
                {name}
                {isCurrentUser && ' (you)'}
            </Text>
        </Space>
    );
};

export const BuildDetailsDrawer: React.FC<BuildDetailsDrawerProps> = ({
    appId,
    orgId,
    buildId,
    bucketId,
    onCancel,
    afterOpenChange,
    isAutoMergeRunwayPRsEnabled,
}) => {
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);

    const build = useSelector((state: RootState) => selectBuildDistributionBuildById(state, buildId));
    const bucket = useSelector((state: RootState) =>
        selectBuildDistributionBucketById(state, build?.bucketId ?? bucketId),
    );

    const userDevices = useSelector((state: RootState) => selectUserDevices(state));
    const hideRegisterDevicePrompt = useSelector(
        (state: RootState) => selectAppSettings(state, appId).shouldHideRegisterDevicePrompt,
    );

    const isSharedWithMe = !bucket;

    const sharedBuild = build as BuildDistroBuildSharedWithMe;
    const sharedByUser = useSelector((state: RootState) =>
        isSharedWithMe && sharedBuild ? selectUserById(state, orgId, appId, sharedBuild.sharedBy) : null,
    );

    const [isInstallationAlertVisible, setIsInstallationAlertVisible] = React.useState(false);

    const [form] = useForm();
    const dispatch = useDispatch();

    const isLoadingBuildDistributionBucketBuild = useIsLoading(
        FetchableEntityType.BuildDistributionBucketBuild,
        buildId,
    );
    const fetchBuildError = useError(
        FetchableEntityType.BuildDistributionBucketBuild,
        buildId,
    ) as BuildDistroEntityError;

    const [buildDistroEntityError, setBuildDistroEntityError] = React.useState<BuildDistroEntityError>(null);

    React.useEffect(() => {
        if (fetchBuildError) {
            if (!Object.values(BuildDistroEntityError).includes(fetchBuildError)) {
                globalMessage.error(fetchBuildError);
            } else {
                setBuildDistroEntityError(fetchBuildError);
            }
        }
    }, [fetchBuildError]);

    React.useEffect(() => {
        if (appId && orgId && buildId && !build) {
            dispatch(
                tryFetchEntity(
                    FetchableEntityType.BuildDistributionBucketBuild,
                    {
                        orgId,
                        appId,
                        buildId,
                        bucketId: bucket?.id ?? bucketId,
                    },
                    buildId,
                ),
            );
        }
    }, [appId, orgId, buildId]);

    const isLoadingWorkItemsTimeline = useIsLoading(
        FetchablePaginatedEntityType.BuildDistributionBuildWorkItemsTimeline,
        buildId,
    );

    const fetchWorkItemsTimelineError = useError(
        FetchablePaginatedEntityType.BuildDistributionBuildWorkItemsTimeline,
        buildId,
    );

    const [workItemsTimelineError, setWorkItemsTimelineError] = React.useState<string>(null);
    React.useEffect(() => {
        if (fetchWorkItemsTimelineError) {
            setWorkItemsTimelineError(fetchWorkItemsTimelineError);
        }
    }, [fetchWorkItemsTimelineError]);

    const {
        limit = 10,
        offset = 0,
        totalCount,
    } = useSelector((state: RootState) => {
        return selectPaginatedBucketBuildWorkItemsTimelineLimitOffsetAndTotalCount(state, buildId);
    });

    const hasNotFetchedAllWorkItems = totalCount > build?.workItemsByBuild?.length;

    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));

    const loadMoreWorkItems = () => {
        dispatch(
            tryFetchPaginatedEntities(
                FetchablePaginatedEntityType.BuildDistributionBuildWorkItemsTimeline,
                {
                    orgId,
                    appId,
                    bucketId: build?.bucketId ?? bucketId,
                    buildId,
                    limit,
                    offset,
                },
                limit,
                offset,
                buildId,
            ),
        );
    };

    const shouldFetchWorkItems = build && build.workItemsByBuild === undefined;

    React.useEffect(() => {
        if (!isDemoInstance && appId && orgId && shouldFetchWorkItems && !isLoadingWorkItemsTimeline) {
            loadMoreWorkItems();
        }
    }, [appId, orgId, buildId, shouldFetchWorkItems]);

    const isUpdatingTesterNotes = useIsLoading(UPDATE_BUILD_DISTRIBUTION_BUILD_TESTER_NOTES, buildId);
    const updateTesterNotesSuccess = useUpdated(UPDATE_BUILD_DISTRIBUTION_BUILD_TESTER_NOTES, buildId);
    const updateTesterNotesError = useError(UPDATE_BUILD_DISTRIBUTION_BUILD_TESTER_NOTES, buildId);

    const isUpdatingBuildTesters = useIsLoading(UPDATE_BUILD_DISTRIBUTION_BUILD_TESTERS, buildId);
    const updateBuildTestersSuccess = useUpdated(UPDATE_BUILD_DISTRIBUTION_BUILD_TESTERS, buildId);
    const updateBuildTestersError = useError(UPDATE_BUILD_DISTRIBUTION_BUILD_TESTERS, buildId);

    const [qrCodeLink, setQRCodeLink] = React.useState(null);
    const [isEditingIndividualTesters, setIsEditingIndividualTesters] = React.useState(false);
    const [newTesters, setNewTesters] = React.useState<string[] | undefined>(build?.individualTesters);

    const appUsersById = useSelector((state: RootState) => {
        const appUsers = selectAppOrgUsers(state, appId);

        const appUsersById = {};
        appUsers.forEach((appUser) => {
            appUsersById[appUser.id] = appUser;
        });

        return appUsersById;
    });

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, orgId));

    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, orgId));
    const currentUser = useSelector((state: RootState) => selectUser(state));

    const individualTestersUsers = useSelector((state: RootState) => {
        if (!build?.individualTesters) {
            return [];
        }
        return compact(
            build.individualTesters?.map((tester) => {
                return selectUserById(state, orgId, appId, tester);
            }),
        );
    });

    const individualTesterItems = individualTestersUsers.map((user) => {
        return {
            title: <TesterRow tester={user} currentUserId={currentUser.id} />,
            description: (
                <Text
                    style={{
                        color: antGrey.grey7,
                    }}
                >
                    Tester
                </Text>
            ),
        };
    });

    const [firstGroup, secondGroup] = build ? buildListDataSource(appId, orgId, build, appPlatform, sharedByUser) : [];

    const app = useSelector((state: RootState) => selectApp(state, appId));
    const [isEditingNotes, setIsEditingNotes] = React.useState(false);

    React.useEffect(() => {
        if (updateTesterNotesSuccess) {
            setIsEditingNotes(false);
            globalMessage.success('Tester notes updated');
        }
        if (updateBuildTestersSuccess) {
            globalMessage.success('Testers updated');
            setIsEditingIndividualTesters(false);
        }
    }, [updateTesterNotesSuccess, updateBuildTestersSuccess]);

    React.useEffect(() => {
        if (updateTesterNotesError) {
            globalMessage.error(updateTesterNotesError);
        }
        if (updateBuildTestersError) {
            globalMessage.error(updateBuildTestersError);
            setIsEditingIndividualTesters(false);
        }
    }, [updateTesterNotesError, updateBuildTestersError]);

    const trackCtaClickedEvent = (ctaType: string) => {
        dispatch(
            trackBuildDistroViewEvent({
                orgId,
                appId,
                bucketId: bucket?.id,
                eventName: AnalyticEvents.Event.BuildDistroClickBuildInstallCta,
                extraProperties: {
                    ctaType,
                },
            }),
        );
    };

    const onClickEditNotes = (e) => {
        setIsEditingNotes(true);
        e.stopPropagation();
    };

    const onClickDoneNotes = (e) => {
        form.submit();
        e.stopPropagation();
    };

    const onClickEditTesters = (e) => {
        setIsEditingIndividualTesters(true);
        e.stopPropagation();
    };

    const onClickDoneEditTesters = (e) => {
        e.stopPropagation();

        const testersAreSame =
            build.individualTesters?.length === newTesters?.length &&
            build.individualTesters?.every((tester) => newTesters.includes(tester));

        if (testersAreSame || newTesters === undefined) {
            setIsEditingIndividualTesters(false);
            return;
        }

        dispatch(
            tryShareBuildDistributionBuildWithTesters({
                appId,
                orgId,
                buildId,
                testers: newTesters,
                bucketId: build.bucketId,
            }),
        );
    };

    const saveTesterNotes = () => {
        form.validateFields().then((values) => {
            const testerNotes = values.testerNotes;
            const request: UpdateBuildDistributionBuildTesterNotesRequest = {
                appId,
                orgId,
                buildId,
                testerNotes,
                bucketId: build?.bucketId,
            };
            dispatch(tryUpdateBuildDistributionBuildTesterNotes(request));
        });
    };

    const missingBinaryBuild = !build?.binaryBuild;
    const disableBasedOnBinaryType =
        build?.binaryBuild ? getShouldDisableInstallationForBinaryType(build.binaryBuild.buildType) : false;
    const disableBasedOnPlatform = !canPlatformInstallBinaries(appPlatform);
    const disableBasedOnPlatformMessage = disableBasedOnPlatform ? PLATFORM_AVAILABILITY_MESSAGE : null;

    const hasBinaryBuild = !!build?.binaryBuild;
    const buildType = build?.binaryBuild?.buildType;

    const hasInstallableProvisioningProfile =
        build?.binaryBuild?.provisioningProfile?.profileType === ProvisioningProfileType.IosAdHoc ||
        build?.binaryBuild?.provisioningProfile?.profileType === ProvisioningProfileType.IosDevelopment;

    const doesUserHaveAtLeastOneDevice = userDevices.length > 0;
    const canVerifyIsAbleToInstall =
        doesUserHaveAtLeastOneDevice &&
        hasInstallableProvisioningProfile &&
        build.binaryBuild.provisioningProfile.devices !== null;

    const isInstallationPossibleForBuildType = buildType ? !getShouldDisableInstallationForBinaryType(buildType) : true;
    const isInstallationPossibleForBinaryBuild = hasBinaryBuild ? isInstallationPossibleForBuildType : false;
    const isInstallingPossibleForPlatform = disableBasedOnPlatform ? false : isInstallationPossibleForBinaryBuild;
    const isInstallingPossible =
        build?.status === BuildDistroBuildStatus.Expired ? false : isInstallingPossibleForPlatform;

    const buildHasExpiredText = 'This build has expired.';

    const appIdentifiers = useSelector((state: RootState) => selectAppIdentifiers(state, appId));

    const distributionServices = useSelector((state: RootState) => selectAppDistributionServices(state, appId));

    const devices = useSelector((state: RootState) => selectUserDevices(state));
    const showShowRegisterDevicesPrompt =
        useSelector((state: RootState) => selectShouldShowRegisterDevicesPrompt(state, devices, appPlatform, orgId)) &&
        shouldShowRegisterDevicesPromptForArtifactType(buildType) &&
        !hideRegisterDevicePrompt;

    return (
        <>
            <Modal open={!!qrCodeLink} onCancel={() => setQRCodeLink(null)} destroyOnClose footer={null}>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <QRCode value={qrCodeLink} size={500} />
                </div>
            </Modal>
            <ResponsiveDrawer
                canCollapseOnMobile
                open={!!buildId}
                onClose={() => {
                    setIsEditingNotes(false);
                    setIsEditingIndividualTesters(false);
                    setNewTesters(undefined);
                    setIsInstallationAlertVisible(false);
                    setWorkItemsTimelineError(null);
                    setBuildDistroEntityError(null);
                    onCancel();
                }}
                afterOpenChange={(isOpen) => {
                    if (typeof afterOpenChange === 'function') {
                        afterOpenChange(isOpen);
                    }
                    if (isOpen) {
                        dispatch(
                            trackBuildDistroViewEvent({
                                orgId,
                                appId,
                                bucketId: bucket?.id,
                                eventName: AnalyticEvents.Event.BuildDistroViewBuildDetails,
                                extraProperties: {
                                    isSharedWithMeBuild: isSharedWithMe,
                                },
                            }),
                        );
                    }
                }}
                header={
                    !isLoadingBuildDistributionBucketBuild &&
                    build &&
                    app && (
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Space size={18}>
                                <BuildBucketAvatar
                                    appImageUrl={appImageUrl}
                                    size={32}
                                    bucket={bucket}
                                    isSharedWithMe={isSharedWithMe}
                                />
                                <BuildNumberTag build={build} backgroundColor={antGrey.grey4} fontSize={16} />
                            </Space>
                        </div>
                    )
                }
                content={
                    <BuildDistroEntityErrorResult errorType={buildDistroEntityError}>
                        {isLoadingBuildDistributionBucketBuild ?
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    width: '100%',
                                    flexGrow: 1,
                                }}
                            >
                                <Spin />
                            </div>
                        :   build && (
                                <DrawerSectionsWrapper>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            gap: 12,
                                            padding: isMobile ? 20 : 0,
                                        }}
                                    >
                                        {isInstallationAlertVisible && (
                                            <Alert
                                                afterClose={() => {
                                                    setIsInstallationAlertVisible(false);
                                                }}
                                                style={
                                                    isMobile ?
                                                        {
                                                            backgroundColor: hexStringToRGBAString(
                                                                runwayColors.primary,
                                                                0.1,
                                                            ),
                                                            borderColor: hexStringToRGBAString(
                                                                runwayColors.primary,
                                                                0.7,
                                                            ),
                                                            marginTop: -6,
                                                        }
                                                    :   {}
                                                }
                                                description="Installation started! Check your home screen for progress."
                                                type="success"
                                                closable
                                            />
                                        )}
                                        <div
                                            style={{
                                                padding: isMobile ? 0 : 16,
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'stretch',
                                                gap: 16,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: isMobile ? '100%' : '50%',
                                                }}
                                            >
                                                {isMobile ?
                                                    <PermissionedActionButton
                                                        block
                                                        style={{
                                                            height: isMobile ? 40 : 'auto',
                                                        }}
                                                        icon={
                                                            <ShareAltOutlined
                                                                style={{
                                                                    color: runwayColors.primary,
                                                                }}
                                                            />
                                                        }
                                                        onClick={() => {
                                                            const url = `${window.location.origin}${createBuildDistroBuildRoute(orgId, appId, build.id, build.bucketId)}`;

                                                            shareLink(url);

                                                            dispatch(
                                                                trackBuildDistroViewEvent({
                                                                    orgId,
                                                                    appId,
                                                                    bucketId: bucket?.id,
                                                                    eventName:
                                                                        AnalyticEvents.Event
                                                                            .BuildDistroClickShareBuildCta,
                                                                }),
                                                            );
                                                        }}
                                                    >
                                                        Share
                                                    </PermissionedActionButton>
                                                :   <BuildDistroShareMenu appId={appId} orgId={orgId} build={build} />}
                                            </div>

                                            {isMobile ?
                                                <AuthedUrlButton
                                                    actionType={RunwayUserAction.UABucketInstallBuilds}
                                                    bucketId={bucketId}
                                                    onLinkAuthed={(authedDownloadLink) => {
                                                        trackCtaClickedEvent('direct');
                                                        window.open(authedDownloadLink, '_self');
                                                        setIsInstallationAlertVisible(true);
                                                    }}
                                                    linkUrl={build.installationUrl}
                                                    type="primary"
                                                    icon={<CloudDownloadOutlined />}
                                                    size="large"
                                                    disabled={
                                                        missingBinaryBuild ||
                                                        disableBasedOnBinaryType ||
                                                        disableBasedOnPlatform
                                                    }
                                                    style={{ width: '100%' }}
                                                >
                                                    Install
                                                </AuthedUrlButton>
                                            :   <BuildDistroInstallMenu
                                                    orgId={orgId}
                                                    appId={appId}
                                                    buildDistroBuild={build}
                                                    shouldShowInfoIcon={true}
                                                    dropDownStyle={{ width: '50%' }}
                                                    bucketId={bucketId}
                                                >
                                                    {(_, isLoading) => (
                                                        <PermissionedActionButton
                                                            block
                                                            actionType={RunwayUserAction.UABucketInstallBuilds}
                                                            bucketId={bucketId}
                                                            icon={<MobileOutlined />}
                                                            type="primary"
                                                            loading={isLoading}
                                                            disabled={false}
                                                            style={{ flexGrow: 1 }}
                                                        >
                                                            <Space style={{ marginLeft: 8 }}>
                                                                Install
                                                                <DownOutlined />
                                                            </Space>
                                                        </PermissionedActionButton>
                                                    )}
                                                </BuildDistroInstallMenu>
                                            }
                                            <ArchiveBuildButton
                                                orgId={orgId}
                                                appId={appId}
                                                bucketId={build.bucketId}
                                                buildId={build.id}
                                            ></ArchiveBuildButton>
                                        </div>
                                        {isInstallingPossible &&
                                            (showShowRegisterDevicesPrompt || canVerifyIsAbleToInstall) && (
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'stretch',
                                                        flexDirection: 'column',
                                                        paddingBottom: 16,
                                                        gap: isMobile ? 16 : 0,
                                                    }}
                                                >
                                                    {showShowRegisterDevicesPrompt && (
                                                        <>
                                                            {isMobile ?
                                                                <RegisterDeviceMobileAlert
                                                                    isInline
                                                                    isClosable={false}
                                                                />
                                                            :   <div
                                                                    style={{
                                                                        padding: '0 24px',
                                                                        width: '100%',
                                                                    }}
                                                                >
                                                                    <RegisterDeviceAlert isInline />
                                                                </div>
                                                            }
                                                        </>
                                                    )}

                                                    {/* TODO: Uncomment when https://linear.app/runway-org/issue/RUN-8362 */}
                                                    {/* {canVerifyIsAbleToInstall && (
                                                        <>
                                                            {isInstallableOnAtLeastOneUserDevice ? (
                                                                <Text style={{ textAlign: 'center' }}>
                                                                    ✅ At least one of your devices is provisioned to
                                                                    install this build.
                                                                </Text>
                                                            ) : (
                                                                <Text style={{ textAlign: 'center' }}>
                                                                    ❌ None of your devices are included in the profile
                                                                    for this build. Unable to install.
                                                                </Text>
                                                            )}
                                                        </>
                                                    )} */}
                                                </div>
                                            )}

                                        {isMobile && disableBasedOnPlatform ?
                                            <Text type="secondary" style={{ textAlign: 'center' }}>
                                                {disableBasedOnPlatformMessage}
                                            </Text>
                                        :   isMobile &&
                                            build.binaryBuild?.buildType && (
                                                <Text type="secondary" style={{ textAlign: 'center' }}>
                                                    {build.status === BuildDistroBuildStatus.Expired ?
                                                        buildHasExpiredText
                                                    :   helperTextForArtifactType(
                                                            build.binaryBuild.buildType,
                                                            build.binaryBuild.provisioningProfile?.name,
                                                        )
                                                    }
                                                </Text>
                                            )
                                        }
                                    </div>

                                    <DrawerSection
                                        title="Notes"
                                        canCollapseOnMobile
                                        headerExtra={
                                            isEditingNotes ?
                                                <PermissionedActionButton
                                                    actionType={RunwayUserAction.UABucketUpdateBuildTestersNotes}
                                                    bucketId={bucketId}
                                                    disabled={isSharedWithMe}
                                                    type="link"
                                                    loading={isUpdatingTesterNotes}
                                                    onClick={onClickDoneNotes}
                                                    icon={<CheckOutlined />}
                                                >
                                                    Apply
                                                </PermissionedActionButton>
                                            :   <PermissionedActionButton
                                                    actionType={RunwayUserAction.UABucketUpdateBuildTestersNotes}
                                                    bucketId={bucketId}
                                                    disabled={isSharedWithMe}
                                                    type="link"
                                                    onClick={onClickEditNotes}
                                                    icon={<EditOutlined />}
                                                >
                                                    Edit
                                                </PermissionedActionButton>
                                        }
                                    >
                                        <div
                                            style={{
                                                padding: '12px',
                                                backgroundColor: antGrey.grey2,
                                                borderRadius: '8px',
                                            }}
                                        >
                                            {isEditingNotes ?
                                                <Form
                                                    form={form}
                                                    preserve={false}
                                                    initialValues={{
                                                        testerNotes: build?.testerNotes,
                                                    }}
                                                    onFinish={saveTesterNotes}
                                                >
                                                    <FormItem noStyle name="testerNotes">
                                                        <MarkdownEditor
                                                            autoFocus={true}
                                                            preview="edit"
                                                            textareaProps={{ disabled: isUpdatingTesterNotes }}
                                                        />
                                                    </FormItem>
                                                </Form>
                                            : build?.testerNotes ?
                                                <MarkdownComponent
                                                    source={build.testerNotes}
                                                    style={{ width: 310 }}
                                                    className="markdown-preview-with-breaks"
                                                />
                                            :   <Text type="secondary">No tester notes have been added.</Text>}
                                        </div>
                                    </DrawerSection>
                                    <DrawerSection title="Build details" canCollapseOnMobile startsCollapsed>
                                        <Space direction="vertical" size={12}>
                                            <DrawerList items={firstGroup ?? []} />
                                            <DrawerList items={secondGroup ?? []} />
                                        </Space>
                                    </DrawerSection>
                                    {Object.keys(build.artifactInfo?.appStoreInfos ?? {}).length > 0 && (
                                        <DrawerSection title="Upload status" canCollapseOnMobile startsCollapsed>
                                            <Space direction="vertical" size={12}>
                                                <ArtifactUploadStatus
                                                    artifact={build.artifactInfo}
                                                    appIdentifiers={appIdentifiers}
                                                    distributionServices={distributionServices}
                                                />
                                            </Space>
                                        </DrawerSection>
                                    )}
                                    <DrawerSection title="Work items diff" canCollapseOnMobile startsCollapsed>
                                        <WorkItemsDiffTimeline
                                            appId={appId}
                                            orgId={orgId}
                                            error={workItemsTimelineError}
                                            isLoading={isLoadingWorkItemsTimeline}
                                            shouldShowLoadMore={hasNotFetchedAllWorkItems}
                                            workItemsByBuild={build?.workItemsByBuild}
                                            onLoadMore={() => loadMoreWorkItems()}
                                            isAutoMergeRunwayPRsEnabled={isAutoMergeRunwayPRsEnabled}
                                        />
                                    </DrawerSection>
                                    {!isSharedWithMe && (
                                        <DrawerSection
                                            title="Shared with"
                                            startsCollapsed
                                            canCollapseOnMobile
                                            headerExtra={
                                                isEditingIndividualTesters ?
                                                    <PermissionedActionButton
                                                        type="link"
                                                        loading={isUpdatingBuildTesters}
                                                        onClick={onClickDoneEditTesters}
                                                        icon={<CheckOutlined />}
                                                    >
                                                        Apply
                                                    </PermissionedActionButton>
                                                :   <PermissionedActionButton
                                                        type="link"
                                                        loading={isUpdatingBuildTesters}
                                                        onClick={onClickEditTesters}
                                                        icon={<EditOutlined />}
                                                        disabled={missingBinaryBuild}
                                                    >
                                                        Edit
                                                    </PermissionedActionButton>
                                            }
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                {isEditingIndividualTesters ?
                                                    <EditTestersForm
                                                        orgUsers={orgUsers}
                                                        currentTesters={individualTestersUsers}
                                                        newTesters={newTesters}
                                                        currentUserId={currentUser.id}
                                                        isLoading={isUpdatingBuildTesters}
                                                        onTestersChange={(testers) => {
                                                            const testerIds = testers.map((tester) => tester.id);
                                                            setNewTesters(testerIds);
                                                        }}
                                                    />
                                                :   <DrawerList
                                                        items={individualTesterItems}
                                                        emptyText="No individual testers"
                                                    />
                                                }
                                            </div>
                                        </DrawerSection>
                                    )}
                                    {bucket && (
                                        <DrawerSection
                                            canCollapseOnMobile
                                            title={`'${bucket.name}' bucket members`}
                                            startsCollapsed
                                        >
                                            <DrawerList
                                                items={buildDrawerMembersListItems(
                                                    bucket.members,
                                                    appUsersById,
                                                    orgGroupsById,
                                                    currentUser,
                                                )}
                                                emptyText="No bucket members"
                                            />
                                        </DrawerSection>
                                    )}
                                    <DrawerSection canCollapseOnMobile title={`Additional artifacts`} startsCollapsed>
                                        <ArtifactsModule
                                            appId={appId}
                                            orgId={orgId}
                                            releaseId={''}
                                            version={''}
                                            artifacts={(build.additionalArtifacts as BuildArtifact[]) ?? []}
                                            services={null}
                                            stabilityMonitoring={null}
                                            appIdentifiers={null}
                                            buildStatus={null}
                                            canRetry={true}
                                            showHeader={false}
                                            isPublic={false}
                                            stepRunComponentProps={null}
                                        />
                                    </DrawerSection>
                                </DrawerSectionsWrapper>
                            )
                        }
                    </BuildDistroEntityErrorResult>
                }
            />
        </>
    );
};
